import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import clsx from 'clsx';
import { CONTACT_TYPE_LABELS, type ContactType, passwordIssues, MODULE_LABELS } from '@mana/shared';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth-context.js';
import { Button } from '../ui/Button.js';
import { Field, Input, Select } from '../ui/Input.js';
import { Badge, Card, CardHeader } from '../ui/Card.js';
import { JsonView } from '../ui/JsonView.js';

interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: string;
  status: string;
  nmls: string | null;
  phone: string | null;
  lastLoginAt: string | null;
  nmlsVerifiedAt: string | null;
  nmlsVerificationNote: string | null;
}

interface AuditEntry {
  id: string;
  action: string;
  entityType: string | null;
  entityId: string | null;
  actorUserId: string | null;
  createdAt: string;
  beforeJson?: string | null;
  afterJson?: string | null;
  ipAddress?: string | null;
}

interface SyncJob {
  id: string;
  provider: string;
  jobType: string;
  status: string;
  recordsFetched: number;
  recordsChanged: number;
  errorSummary: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  requestedByUserId: string | null;
}

interface SyncState {
  lastSuccessfulSyncAt: string | null;
  deltaCursor: string | null;
  fullSyncInProgress: boolean;
}

const MODULES = [
  'accounting',
  'pipeline_tracking',
  'call_reports',
  'reporting',
  'crm',
  'admin_tasks',
  'workflows',
] as const;

export function AdminPage() {
  const usersQuery = useQuery<{ users: AdminUser[] }>({
    queryKey: ['admin-users'],
    queryFn: () => api('/api/admin/users'),
  });
  const auditQuery = useQuery<{ logs: AuditEntry[] }>({
    queryKey: ['admin-audit'],
    queryFn: () => api('/api/admin/audit-log?limit=200'),
    refetchInterval: 15_000,
  });
  const syncQuery = useQuery<{ jobs: SyncJob[]; state: SyncState | null }>({
    queryKey: ['admin-sync-jobs'],
    queryFn: () => api('/api/sync/jobs'),
    refetchInterval: 5_000,
  });

  return (
    <div className="space-y-6">
      <header>
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-gold-700">
          Administration
        </p>
        <h1 className="mt-1 font-display text-3xl font-bold text-brand-navy-800">
          Users, sync, & audit
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Manage roles, troubleshoot UWM sync jobs, and review the operational audit trail.
        </p>
      </header>

      <CommunicationKillSwitch />
      <UsersAndPermissions users={usersQuery.data?.users ?? []} />
      <UwmFieldsManager />
      <ContactsManager />
      <SyncJobsPanel jobs={syncQuery.data?.jobs ?? []} state={syncQuery.data?.state ?? null} />
      <AuditLog logs={auditQuery.data?.logs ?? []} />
    </div>
  );
}

// ===========================================================================
// Communication kill-switch — toggle outbound email + WhatsApp org-wide for
// maintenance windows. Affects workflow-triggered sends, loan-drawer manual
// sends, and the Integrations test buttons. Disabled sends still get a row
// in NotificationLog (status='skipped') so the activity panel shows them.
// ===========================================================================

interface CommunicationToggles {
  outboundEmailEnabled: boolean;
  outboundWhatsappEnabled: boolean;
}

function CommunicationKillSwitch() {
  const qc = useQueryClient();
  const toggles = useQuery<CommunicationToggles>({
    queryKey: ['admin-communication-toggles'],
    queryFn: () => api('/api/admin/communication-toggles'),
  });
  const mutation = useMutation({
    mutationFn: (body: {
      outboundEmailEnabled?: boolean;
      outboundWhatsappEnabled?: boolean;
    }) =>
      api<CommunicationToggles>('/api/admin/communication-toggles', {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: (next) => {
      qc.setQueryData(['admin-communication-toggles'], next);
    },
  });

  const emailOn = toggles.data?.outboundEmailEnabled ?? true;
  const whatsappOn = toggles.data?.outboundWhatsappEnabled ?? true;
  const anyOff = !emailOn || !whatsappOn;

  return (
    <Card
      className={
        anyOff
          ? 'border-rose-300 bg-rose-50/40'
          : undefined
      }
    >
      <CardHeader
        title="Communication kill-switch"
        subtitle="Stop all outbound email or WhatsApp org-wide during maintenance. Workflows and Test Sends both honor these. Disabled sends still appear in the loan activity panel as 'skipped (outbound_*_disabled)'."
      />
      <div className="grid grid-cols-1 gap-3 px-5 pb-5 md:grid-cols-2">
        <KillSwitchRow
          label="Outbound Email"
          channel="email"
          on={emailOn}
          disabled={mutation.isPending || toggles.isLoading}
          onToggle={(next) => mutation.mutate({ outboundEmailEnabled: next })}
        />
        <KillSwitchRow
          label="Outbound WhatsApp"
          channel="whatsapp"
          on={whatsappOn}
          disabled={mutation.isPending || toggles.isLoading}
          onToggle={(next) => mutation.mutate({ outboundWhatsappEnabled: next })}
        />
      </div>
      {anyOff && (
        <div className="mx-5 mb-5 rounded-md bg-rose-100 px-3 py-2 text-xs text-rose-900 ring-1 ring-rose-300">
          ⚠ Maintenance mode active for{' '}
          {!emailOn && !whatsappOn
            ? 'EMAIL + WHATSAPP'
            : !emailOn
              ? 'EMAIL'
              : 'WHATSAPP'}{' '}
          — outbound messages on the disabled channel(s) are being suppressed.
          Flip them back on once your maintenance window closes.
        </div>
      )}
      {mutation.isError && (
        <div className="mx-5 mb-5 rounded-md bg-rose-100 px-3 py-2 text-xs text-rose-900 ring-1 ring-rose-300">
          {mutation.error instanceof Error ? mutation.error.message : 'Save failed'}
        </div>
      )}
    </Card>
  );
}

function KillSwitchRow({
  label,
  channel,
  on,
  disabled,
  onToggle,
}: {
  label: string;
  channel: 'email' | 'whatsapp';
  on: boolean;
  disabled: boolean;
  onToggle: (next: boolean) => void;
}) {
  return (
    <div
      className={clsx(
        'flex items-center justify-between gap-3 rounded-xl border px-4 py-3',
        on
          ? 'border-emerald-200 bg-emerald-50/60'
          : 'border-rose-300 bg-rose-50',
      )}
    >
      <div>
        <div className="flex items-center gap-2">
          <span
            className={clsx(
              'text-base',
              channel === 'email' ? 'text-brand-navy-700' : 'text-emerald-700',
            )}
          >
            {channel === 'email' ? '✉' : '💬'}
          </span>
          <span className="font-display font-semibold text-brand-navy-800">
            {label}
          </span>
          <Badge tone={on ? 'success' : 'danger'}>
            {on ? 'ON' : 'OFF (suppressed)'}
          </Badge>
        </div>
        <p className="mt-1 text-[11px] text-slate-500">
          {on
            ? 'Sends are flowing through the configured provider.'
            : 'All outbound messages on this channel are being skipped.'}
        </p>
      </div>
      <Button
        variant={on ? 'danger' : 'gold'}
        size="sm"
        disabled={disabled}
        onClick={() => {
          if (on) {
            const ok = window.confirm(
              `Disable outbound ${channel}? All workflow-triggered, test-send, and loan-drawer messages on this channel will be SUPPRESSED until you turn it back on.`,
            );
            if (!ok) return;
          }
          onToggle(!on);
        }}
      >
        {on ? 'Turn OFF' : 'Turn back ON'}
      </Button>
    </div>
  );
}

function UsersAndPermissions({ users }: { users: AdminUser[] }) {
  const [activeUserId, setActiveUserId] = useState<string | null>(null);

  return (
    <Card>
      <CardHeader title="Users" subtitle="Click Manage to set per-module access." />
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-2.5">Email</th>
              <th className="px-4 py-2.5">Name</th>
              <th className="px-4 py-2.5">Role</th>
              <th className="px-4 py-2.5">Status</th>
              <th className="px-4 py-2.5">NMLS</th>
              <th className="px-4 py-2.5">Phone (for WhatsApp)</th>
              <th className="px-4 py-2.5">Last login</th>
              <th className="px-4 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <React.Fragment key={u.id}>
                <tr className="border-t border-slate-100">
                  <td className="px-4 py-2.5 font-medium text-brand-navy-800">{u.email}</td>
                  <td className="px-4 py-2.5">
                    <NameEditor userId={u.id} initial={u.name} />
                  </td>
                  <td className="px-4 py-2.5">
                    <Badge tone={u.role === 'admin' ? 'gold' : 'info'}>{u.role}</Badge>
                  </td>
                  <td className="px-4 py-2.5">
                    <Badge
                      tone={
                        u.status === 'active'
                          ? 'success'
                          : u.status === 'pending_nmls_review'
                            ? 'gold'
                            : 'neutral'
                      }
                    >
                      {u.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-2.5">
                    <NmlsEditor userId={u.id} initial={u.nmls} />
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <NmlsVerifyBadge user={u} />
                      <NmlsReverifyButton user={u} />
                    </div>
                  </td>
                  <td className="px-4 py-2.5">
                    <PhoneEditor userId={u.id} initial={u.phone} />
                  </td>
                  <td className="px-4 py-2.5 text-slate-500">
                    {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : '—'}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setActiveUserId(activeUserId === u.id ? null : u.id)}
                    >
                      {activeUserId === u.id ? 'Close' : 'Manage'}
                    </Button>
                  </td>
                </tr>
                {activeUserId === u.id && (
                  <tr className="bg-slate-50/60">
                    <td colSpan={8} className="px-4 py-4">
                      <UserManagePanel user={u} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function NameEditor({ userId, initial }: { userId: string; initial: string }) {
  const qc = useQueryClient();
  const [value, setValue] = useState(initial);
  const [dirty, setDirty] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () =>
      api('/api/admin/users/name', {
        method: 'PUT',
        body: JSON.stringify({ userId, name: value.trim() }),
      }),
    onSuccess: () => {
      setDirty(false);
      setErr(null);
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      qc.invalidateQueries({ queryKey: ['admin-audit'] });
    },
    onError: (e: Error) => setErr(e.message),
  });
  return (
    <div className="flex items-center gap-1.5">
      <input
        type="text"
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          setDirty(true);
        }}
        className="w-40 rounded-md border border-slate-300 px-2 py-1 text-xs shadow-sm focus:border-brand-navy-400 focus:outline-none focus:ring-1 focus:ring-brand-navy-200"
      />
      {dirty && value.trim().length > 0 && (
        <button
          onClick={() => save.mutate()}
          disabled={save.isPending}
          className="rounded-md bg-brand-navy-700 px-2 py-1 text-[11px] font-medium text-white hover:bg-brand-navy-800 disabled:opacity-50"
        >
          {save.isPending ? '…' : 'Save'}
        </button>
      )}
      {err && <span className="text-[10px] text-rose-600">{err}</span>}
    </div>
  );
}

const PLACEHOLDER_NMLS = '0000000';

/** NMLS review panel in the Manage view: shows the check result and — for a held
 *  or unverified account — a link to NMLS Consumer Access plus a "mark verified"
 *  action (which activates a held account so the user can finally sign in). */
function NmlsReviewSection({ user }: { user: AdminUser }) {
  const qc = useQueryClient();
  const [msg, setMsg] = useState<string | null>(null);
  const held = user.status === 'pending_nmls_review';
  const hasNmls = !!user.nmls && user.nmls !== PLACEHOLDER_NMLS;
  const unverified = !user.nmlsVerifiedAt;
  const mark = useMutation({
    mutationFn: () =>
      api<{ activated: boolean }>('/api/admin/users/nmls-mark-verified', {
        method: 'POST',
        body: JSON.stringify({ userId: user.id }),
      }),
    onSuccess: (r) => {
      setMsg(r.activated ? 'NMLS verified — account activated ✓' : 'NMLS marked verified ✓');
      void qc.invalidateQueries({ queryKey: ['admin-users'] });
      void qc.invalidateQueries({ queryKey: ['admin-audit'] });
    },
    onError: (e: Error) => setMsg(e.message),
  });

  if (!user.nmlsVerificationNote && !held && !unverified) return null;

  return (
    <div
      className={
        'rounded-lg border px-3 py-3 text-[11px] ' +
        (held ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-white')
      }
    >
      <div className="flex items-center justify-between">
        <span className="font-semibold uppercase tracking-wider text-slate-500">
          NMLS {held ? 'review required' : 'check'}
        </span>
        {user.nmlsVerifiedAt && <span className="font-semibold text-emerald-600">✓ verified</span>}
      </div>
      {user.nmlsVerificationNote && (
        <p className="mt-1 text-slate-600">
          {user.nmlsVerifiedAt ? '✓' : '⚠'} {user.nmlsVerificationNote}
        </p>
      )}
      {held && (
        <p className="mt-1 text-amber-800">
          This account is held and <strong>cannot sign in</strong> until you verify their NMLS on the
          official records and approve it.
        </p>
      )}
      {(held || unverified) && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <a
            href="https://www.nmlsconsumeraccess.org/"
            target="_blank"
            rel="noreferrer"
            className="rounded-md border border-slate-300 px-2 py-1 font-medium text-brand-navy-700 hover:bg-slate-100"
          >
            Look up on NMLS Consumer Access ↗{hasNmls ? ` (NMLS #${user.nmls})` : ''}
          </a>
          <button
            type="button"
            onClick={() => mark.mutate()}
            disabled={mark.isPending}
            className="rounded-md bg-emerald-600 px-2.5 py-1 font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {mark.isPending ? 'Marking…' : held ? 'Mark NMLS verified & activate' : 'Mark NMLS verified'}
          </button>
          {msg && <span className="text-slate-600">{msg}</span>}
        </div>
      )}
    </div>
  );
}

/** Small badge under the NMLS field showing the sign-up NMLS-verification result. */
function NmlsVerifyBadge({ user }: { user: AdminUser }) {
  // Only meaningful once a real NMLS is on file.
  if (!user.nmls || user.nmls === PLACEHOLDER_NMLS) return null;
  const verified = !!user.nmlsVerifiedAt;
  return (
    <div
      title={
        user.nmlsVerificationNote ??
        (verified ? 'Verified against NMLS records' : 'Not verified against NMLS records')
      }
      className={
        'mt-1 inline-block rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ' +
        (verified ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700')
      }
    >
      {verified ? 'NMLS ✓' : 'NMLS unverified'}
    </div>
  );
}

/** Admin / super-admin button to re-run NMLS verification for a user on demand. */
function NmlsReverifyButton({ user }: { user: AdminUser }) {
  const qc = useQueryClient();
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const run = useMutation({
    mutationFn: () =>
      api<{ verified: boolean; note: string; message: string | null }>(
        '/api/admin/users/verify-nmls',
        { method: 'POST', body: JSON.stringify({ userId: user.id }) },
      ),
    onSuccess: (r) => {
      setMsg({ ok: r.verified, text: r.message ?? r.note });
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      qc.invalidateQueries({ queryKey: ['admin-audit'] });
    },
    onError: (e: Error) => setMsg({ ok: false, text: e.message }),
  });
  // Nothing to verify without a real NMLS on file.
  if (!user.nmls || user.nmls === PLACEHOLDER_NMLS) return null;
  return (
    <>
      <button
        type="button"
        onClick={() => run.mutate()}
        disabled={run.isPending}
        title="Re-check this NMLS against official NMLS records (can take up to a minute)"
        className="rounded-md border border-slate-300 px-1.5 py-0.5 text-[10px] font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-50"
      >
        {run.isPending ? 'Verifying…' : 'Re-verify'}
      </button>
      {msg && (
        <p className={'mt-1 basis-full text-[10px] ' + (msg.ok ? 'text-emerald-600' : 'text-amber-700')}>
          {msg.ok ? '✓ ' : '⚠ '}
          {msg.text}
        </p>
      )}
    </>
  );
}

function NmlsEditor({
  userId,
  initial,
}: {
  userId: string;
  initial: string | null;
}) {
  const qc = useQueryClient();
  const [value, setValue] = useState(initial && initial !== PLACEHOLDER_NMLS ? initial : '');
  const [dirty, setDirty] = useState(false);
  const isPlaceholder = !initial || initial === PLACEHOLDER_NMLS;

  const save = useMutation({
    mutationFn: () =>
      api('/api/admin/users/nmls', {
        method: 'PUT',
        body: JSON.stringify({ userId, nmls: value.trim() || undefined }),
      }),
    onSuccess: () => {
      setDirty(false);
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      qc.invalidateQueries({ queryKey: ['admin-audit'] });
    },
  });

  return (
    <div className="flex items-center gap-1.5">
      <input
        type="text"
        inputMode="numeric"
        value={value}
        onChange={(e) => {
          setValue(e.target.value.replace(/\D/g, ''));
          setDirty(true);
        }}
        placeholder={PLACEHOLDER_NMLS}
        className="w-24 rounded-md border border-slate-300 px-2 py-1 text-xs font-mono shadow-sm focus:border-brand-navy-400 focus:outline-none focus:ring-1 focus:ring-brand-navy-200"
      />
      {isPlaceholder && !dirty && (
        <span
          title="Placeholder — LO has not set their NMLS"
          className="rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-700"
        >
          Default
        </span>
      )}
      {dirty && (
        <button
          onClick={() => save.mutate()}
          disabled={save.isPending}
          className="rounded-md bg-brand-navy-700 px-2 py-1 text-[11px] font-medium text-white hover:bg-brand-navy-800 disabled:opacity-50"
        >
          {save.isPending ? '…' : 'Save'}
        </button>
      )}
    </div>
  );
}

/**
 * Set/clear a user's phone number. Used for MLO WhatsApp delivery: the
 * notification resolver matches a loan's loan-officer NMLS to a User and
 * reads this phone. Store in E.164 (e.g. +13125550123).
 */
function PhoneEditor({ userId, initial }: { userId: string; initial: string | null }) {
  const qc = useQueryClient();
  const [value, setValue] = useState(initial ?? '');
  const [dirty, setDirty] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      api('/api/admin/users/phone', {
        method: 'PUT',
        body: JSON.stringify({ userId, phone: value.trim() || null }),
      }),
    onSuccess: () => {
      setDirty(false);
      setErr(null);
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      qc.invalidateQueries({ queryKey: ['admin-audit'] });
    },
    onError: (e: Error) => setErr(e.message),
  });

  // Light client hint: WhatsApp needs a country code. Warn (don't block) if no +.
  const looksOk = value.trim() === '' || /^\+?\d[\d\s().-]{6,}$/.test(value.trim());

  return (
    <div className="flex items-center gap-1.5">
      <input
        type="tel"
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          setDirty(true);
        }}
        placeholder="+13125550123"
        title="E.164 format with country code, e.g. +13125550123"
        className="w-36 rounded-md border border-slate-300 px-2 py-1 text-xs font-mono shadow-sm focus:border-brand-navy-400 focus:outline-none focus:ring-1 focus:ring-brand-navy-200"
      />
      {value.trim() !== '' && !value.trim().startsWith('+') && (
        <span
          title="Add a country code (e.g. +1) so WhatsApp can deliver"
          className="rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-700"
        >
          + ?
        </span>
      )}
      {dirty && (
        <button
          onClick={() => save.mutate()}
          disabled={save.isPending || !looksOk}
          title={!looksOk ? 'Enter a valid phone number' : undefined}
          className="rounded-md bg-brand-navy-700 px-2 py-1 text-[11px] font-medium text-white hover:bg-brand-navy-800 disabled:opacity-50"
        >
          {save.isPending ? '…' : 'Save'}
        </button>
      )}
      {err && <span className="text-[10px] text-rose-600">{err}</span>}
    </div>
  );
}

const ASSIGNABLE_ROLES = ['mlo', 'processor', 'readonly', 'admin'] as const;

/**
 * Expanded "Manage" panel for a user: role, account status, password, and the
 * per-module access grid. Guards mirror the API — an ordinary admin can't touch
 * a super-admin, and nobody can disable their own account.
 */
function UserManagePanel({ user }: { user: AdminUser }) {
  const { me } = useAuth();
  const isSuperAdmin = me?.user.role === 'super_admin';
  const isSelf = me?.user.id === user.id;
  const locked = user.role === 'super_admin' && !isSuperAdmin;

  return (
    <div className="space-y-5">
      <NmlsReviewSection user={user} />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <RoleEditor user={user} isSuperAdmin={isSuperAdmin} disabled={locked} />
        <StatusControl user={user} disabled={locked || isSelf} selfNote={isSelf} />
        <PasswordSetter user={user} disabled={locked} />
      </div>
      <PermissionsEditor userId={user.id} />
    </div>
  );
}

function RoleEditor({
  user,
  isSuperAdmin,
  disabled,
}: {
  user: AdminUser;
  isSuperAdmin: boolean;
  disabled: boolean;
}) {
  const qc = useQueryClient();
  const [role, setRole] = useState(user.role);
  const [err, setErr] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (r: string) =>
      api('/api/admin/users/role', { method: 'PUT', body: JSON.stringify({ userId: user.id, role: r }) }),
    onSuccess: () => {
      setErr(null);
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      qc.invalidateQueries({ queryKey: ['admin-audit'] });
    },
    onError: (e: Error) => {
      setErr(e.message);
      setRole(user.role);
    },
  });
  // Always include the user's current role so the controlled <select> has a match.
  const options = Array.from(
    new Set([...(isSuperAdmin ? [...ASSIGNABLE_ROLES, 'super_admin'] : ASSIGNABLE_ROLES), user.role]),
  );

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Role</div>
      <Select
        className="mt-2"
        value={role}
        disabled={disabled || save.isPending}
        onChange={(e) => {
          const r = e.target.value;
          setRole(r);
          save.mutate(r);
        }}
      >
        {options.map((r) => (
          <option key={r} value={r}>
            {r}
          </option>
        ))}
      </Select>
      <p className="mt-1 text-[11px] text-slate-500">
        Admin / super admin get the Administration panel. MLO = regular member.
      </p>
      {err && <p className="mt-1 text-[11px] text-rose-600">{err}</p>}
    </div>
  );
}

function StatusControl({
  user,
  disabled,
  selfNote,
}: {
  user: AdminUser;
  disabled: boolean;
  selfNote: boolean;
}) {
  const qc = useQueryClient();
  const [err, setErr] = useState<string | null>(null);
  const isActive = user.status === 'active';
  const target = isActive ? 'disabled' : 'active';
  const save = useMutation({
    mutationFn: () =>
      api('/api/admin/users/status', {
        method: 'PUT',
        body: JSON.stringify({ userId: user.id, status: target }),
      }),
    onSuccess: () => {
      setErr(null);
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      qc.invalidateQueries({ queryKey: ['admin-audit'] });
    },
    onError: (e: Error) => setErr(e.message),
  });

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
        Account status
      </div>
      <div className="mt-2 flex items-center gap-2">
        <Badge tone={isActive ? 'success' : user.status === 'pending_verification' ? 'gold' : 'neutral'}>
          {user.status}
        </Badge>
        <Button
          size="sm"
          variant={isActive ? 'ghost' : 'secondary'}
          disabled={disabled || save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? '…' : isActive ? 'Disable' : 'Activate'}
        </Button>
      </div>
      {selfNote ? (
        <p className="mt-1 text-[11px] text-slate-400">You can't change your own status.</p>
      ) : (
        <p className="mt-1 text-[11px] text-slate-500">
          {isActive
            ? 'Disabling blocks sign-in.'
            : 'Activating allows sign-in (skips email verification).'}
        </p>
      )}
      {err && <p className="mt-1 text-[11px] text-rose-600">{err}</p>}
    </div>
  );
}

function PasswordSetter({ user, disabled }: { user: AdminUser; disabled: boolean }) {
  const qc = useQueryClient();
  const [pw, setPw] = useState('');
  const [show, setShow] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const issues = pw ? passwordIssues(pw) : [];
  const save = useMutation({
    mutationFn: () =>
      api('/api/admin/users/password', {
        method: 'PUT',
        body: JSON.stringify({ userId: user.id, password: pw }),
      }),
    onSuccess: () => {
      setMsg('Password updated ✓');
      setErr(null);
      setPw('');
      qc.invalidateQueries({ queryKey: ['admin-audit'] });
    },
    onError: (e: Error) => {
      setErr(e.message);
      setMsg(null);
    },
  });

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
        Set password
      </div>
      <div className="mt-2 flex items-center gap-1.5">
        <input
          type={show ? 'text' : 'password'}
          value={pw}
          disabled={disabled}
          onChange={(e) => {
            setPw(e.target.value);
            setMsg(null);
            setErr(null);
          }}
          placeholder="New password"
          autoComplete="new-password"
          className="w-full rounded-md border border-slate-300 px-2 py-1 text-xs shadow-sm focus:border-brand-navy-400 focus:outline-none focus:ring-1 focus:ring-brand-navy-200 disabled:bg-slate-100"
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          className="shrink-0 text-[11px] text-slate-500 hover:text-slate-700"
        >
          {show ? 'Hide' : 'Show'}
        </button>
      </div>
      {pw && issues.length > 0 && (
        <ul className="mt-1 space-y-0.5 text-[10px] text-slate-400">
          {issues.map((i) => (
            <li key={i}>• {i}</li>
          ))}
        </ul>
      )}
      <div className="mt-2 flex items-center gap-2">
        <button
          onClick={() => save.mutate()}
          disabled={disabled || save.isPending || pw.length === 0 || issues.length > 0}
          className="rounded-md bg-brand-navy-700 px-2.5 py-1 text-[11px] font-medium text-white hover:bg-brand-navy-800 disabled:opacity-50"
        >
          {save.isPending ? 'Saving…' : 'Set password'}
        </button>
        {msg && <span className="text-[11px] text-emerald-600">{msg}</span>}
      </div>
      {err && <p className="mt-1 text-[11px] text-rose-600">{err}</p>}
    </div>
  );
}

type ModuleLevel = 'none' | 'view' | 'edit' | 'admin';

const MODULE_LEVELS: Array<{
  key: ModuleLevel;
  label: string;
  body: { canView: boolean; canEdit: boolean; canAdmin: boolean };
}> = [
  { key: 'view', label: 'View', body: { canView: true, canEdit: false, canAdmin: false } },
  { key: 'edit', label: 'Edit', body: { canView: true, canEdit: true, canAdmin: false } },
  { key: 'admin', label: 'Admin', body: { canView: true, canEdit: true, canAdmin: true } },
  { key: 'none', label: 'Revoke', body: { canView: false, canEdit: false, canAdmin: false } },
];

function levelOf(p?: { canView: boolean; canEdit: boolean; canAdmin: boolean }): ModuleLevel {
  if (!p) return 'none';
  if (p.canAdmin) return 'admin';
  if (p.canEdit) return 'edit';
  if (p.canView) return 'view';
  return 'none';
}

function PermissionsEditor({ userId }: { userId: string }) {
  const qc = useQueryClient();
  const permsQuery = useQuery<{
    permissions: Record<string, { canView: boolean; canEdit: boolean; canAdmin: boolean }>;
  }>({
    queryKey: ['admin-user-permissions', userId],
    queryFn: () => api(`/api/admin/users/${userId}/permissions`),
  });
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const set = useMutation({
    mutationFn: (body: {
      userId: string;
      moduleKey: string;
      canView: boolean;
      canEdit: boolean;
      canAdmin: boolean;
    }) => api('/api/admin/module-permissions', { method: 'PUT', body: JSON.stringify(body) }),
    onMutate: (b) => {
      setSavingKey(b.moduleKey);
      setErr(null);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-user-permissions', userId] });
      qc.invalidateQueries({ queryKey: ['admin-audit'] });
    },
    onError: (e: Error) => setErr(e.message),
    onSettled: () => setSavingKey(null),
  });
  const perms = permsQuery.data?.permissions ?? {};

  return (
    <div>
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
        Module access
        {permsQuery.isLoading && <span className="text-slate-400">· loading…</span>}
      </div>
      {err && <p className="mt-1 text-[11px] text-rose-600">Couldn't save: {err}</p>}
      <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-4">
        {MODULES.map((m) => {
          const current = levelOf(perms[m]);
          const saving = savingKey === m;
          return (
            <div key={m} className="rounded-xl border border-slate-200 bg-white p-3">
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-brand-navy-800">{MODULE_LABELS[m]}</div>
                {saving && <span className="text-[10px] text-slate-400">saving…</span>}
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                {MODULE_LEVELS.map((lvl) => {
                  const active = current === lvl.key;
                  return (
                    <button
                      key={lvl.key}
                      type="button"
                      onClick={() => set.mutate({ userId, moduleKey: m, ...lvl.body })}
                      disabled={set.isPending}
                      aria-pressed={active}
                      className={
                        'rounded-md px-2 py-1 text-[11px] font-medium transition-colors disabled:opacity-50 ' +
                        (active
                          ? lvl.key === 'none'
                            ? 'bg-slate-200 text-slate-700'
                            : lvl.key === 'admin'
                              ? 'bg-amber-500 text-white'
                              : 'bg-brand-navy-700 text-white'
                          : 'border border-slate-300 text-slate-600 hover:bg-slate-100')
                      }
                    >
                      {lvl.label}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      <p className="mt-2 text-[11px] text-slate-400">
        The highlighted level is the user's current access. Note: admins &amp; super-admins bypass
        these per-module checks — grants here take effect for MLO / processor / read-only members.
      </p>
    </div>
  );
}

function SyncJobsPanel({ jobs, state }: { jobs: SyncJob[]; state: SyncState | null }) {
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);

  const lastSuccess = state?.lastSuccessfulSyncAt
    ? new Date(state.lastSuccessfulSyncAt).toLocaleString()
    : '—';
  const cursor = state?.deltaCursor ? new Date(state.deltaCursor).toLocaleString() : '—';

  return (
    <Card>
      <CardHeader
        title="UWM sync jobs"
        subtitle={`Last successful sync: ${lastSuccess}. Delta cursor: ${cursor}.`}
      />
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-2.5">Created</th>
              <th className="px-4 py-2.5">Type</th>
              <th className="px-4 py-2.5">Status</th>
              <th className="px-4 py-2.5">Fetched</th>
              <th className="px-4 py-2.5">Changed</th>
              <th className="px-4 py-2.5">Duration</th>
              <th className="px-4 py-2.5">Error</th>
              <th className="px-4 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {jobs.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-sm text-slate-500">
                  No sync jobs yet. The worker enqueues an initial sync on first boot.
                </td>
              </tr>
            )}
            {jobs.map((j) => {
              const duration =
                j.startedAt && j.completedAt
                  ? ((new Date(j.completedAt).getTime() - new Date(j.startedAt).getTime()) / 1000).toFixed(1) + 's'
                  : '—';
              return (
                <React.Fragment key={j.id}>
                  <tr className="border-t border-slate-100">
                    <td className="px-4 py-2.5 text-slate-500">
                      {new Date(j.createdAt).toLocaleString()}
                    </td>
                    <td className="px-4 py-2.5">
                      <Badge tone={j.jobType.startsWith('manual') ? 'gold' : 'info'}>{j.jobType}</Badge>
                    </td>
                    <td className="px-4 py-2.5">
                      <Badge tone={statusTone(j.status)}>{j.status}</Badge>
                    </td>
                    <td className="px-4 py-2.5 text-slate-700">{j.recordsFetched}</td>
                    <td className="px-4 py-2.5 text-slate-700">{j.recordsChanged}</td>
                    <td className="px-4 py-2.5 text-slate-700">{duration}</td>
                    <td className="px-4 py-2.5 max-w-[320px]">
                      <span className="block truncate text-xs text-rose-600" title={j.errorSummary ?? ''}>
                        {j.errorSummary ?? ''}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setExpandedJobId(expandedJobId === j.id ? null : j.id)}
                      >
                        {expandedJobId === j.id ? 'Close' : 'Details'}
                      </Button>
                    </td>
                  </tr>
                  {expandedJobId === j.id && <SyncJobDetail jobId={j.id} />}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function SyncJobDetail({ jobId }: { jobId: string }) {
  // Pull the matching sync.* audit entries which carry the rich JSON afterJson.
  const auditQuery = useQuery<{ logs: AuditEntry[] }>({
    queryKey: ['admin-audit-for-job', jobId],
    queryFn: () => api(`/api/admin/audit-log?limit=200`),
  });
  const matches = (auditQuery.data?.logs ?? []).filter(
    (l) => l.entityType === 'SyncJob' && l.entityId === jobId,
  );

  return (
    <tr className="bg-slate-50/60">
      <td colSpan={8} className="px-4 py-4">
        {matches.length === 0 ? (
          <div className="text-xs text-slate-500">
            No detailed audit entries yet. They appear after the worker finishes the job.
          </div>
        ) : (
          <div className="space-y-3">
            {matches.map((m) => (
              <div key={m.id}>
                <div className="mb-1 flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-500">
                  <Badge tone={m.action === 'sync.failed' ? 'danger' : 'success'}>
                    {m.action}
                  </Badge>
                  <span>{new Date(m.createdAt).toLocaleString()}</span>
                </div>
                <JsonView value={m.afterJson} />
              </div>
            ))}
          </div>
        )}
      </td>
    </tr>
  );
}

function statusTone(s: string): 'success' | 'warning' | 'danger' | 'info' | 'neutral' {
  if (s === 'success') return 'success';
  if (s === 'running' || s === 'queued') return 'info';
  if (s === 'failed') return 'danger';
  if (s === 'partial') return 'warning';
  return 'neutral';
}

// ===========================================================================
// UWM field manager — super-admin only. Controls which UWM Insights fields
// the sync worker requests in its GraphQL query.
// ===========================================================================

interface UwmFieldRow {
  fieldName: string;
  isBaseline: boolean;
  isInUwmCatalog: boolean;
  supported: boolean;
  manuallyDisabled: boolean;
  extraIncluded: boolean;
  adminNote: string | null;
  lastRejectedAt: string | null;
  rejectionNote: string | null;
  updatedBy: string | null;
  updatedAt: string | null;
  activelyPulled: boolean;
}

interface UwmFieldsResponse {
  fields: UwmFieldRow[];
  catalogSize: number;
  baselineSize: number;
  activeCount: number;
}

function UwmFieldsManager() {
  const { me } = useAuth();
  const isSuperAdmin = me?.user.role === 'super_admin';
  const qc = useQueryClient();
  const [activeSearch, setActiveSearch] = useState('');
  const [addQuery, setAddQuery] = useState('');
  const [showAdd, setShowAdd] = useState(false);

  const fields = useQuery<UwmFieldsResponse>({
    queryKey: ['admin-uwm-fields'],
    queryFn: () => api('/api/admin/uwm-fields'),
    enabled: isSuperAdmin,
  });

  const togglePulled = useMutation({
    mutationFn: (args: {
      fieldName: string;
      manuallyDisabled?: boolean;
      extraIncluded?: boolean;
    }) =>
      api(`/api/admin/uwm-fields/${args.fieldName}`, {
        method: 'PUT',
        body: JSON.stringify({
          ...(args.manuallyDisabled !== undefined && {
            manuallyDisabled: args.manuallyDisabled,
          }),
          ...(args.extraIncluded !== undefined && { extraIncluded: args.extraIncluded }),
        }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-uwm-fields'] }),
  });

  if (!isSuperAdmin) {
    // Admins (non-super) do not even see this card. Keep RBAC tight at the UI
    // layer too — server still enforces it.
    return null;
  }

  const allFields = fields.data?.fields ?? [];
  const pulled = allFields.filter((f) => f.activelyPulled);
  const notPulled = allFields.filter((f) => !f.activelyPulled && f.isInUwmCatalog);

  const visiblePulled = pulled.filter((f) =>
    f.fieldName.toLowerCase().includes(activeSearch.trim().toLowerCase()),
  );

  const trimmedAdd = addQuery.trim().toLowerCase();
  const suggestions = trimmedAdd
    ? notPulled
        .filter((f) => f.fieldName.toLowerCase().includes(trimmedAdd))
        .slice(0, 12)
    : [];

  const activeCount = fields.data?.activeCount ?? pulled.length;
  const totalCatalog = fields.data?.catalogSize ?? 0;

  return (
    <Card>
      <CardHeader
        title="UWM Insights API fields"
        subtitle={`Super-administrator only. The sync worker is currently pulling ${activeCount} of ${totalCatalog} documented UWM Loans fields.`}
        actions={
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={activeSearch}
              onChange={(e) => setActiveSearch(e.target.value)}
              placeholder="Search pulled…"
              className="w-44 rounded-md border border-slate-300 px-2 py-1 text-xs shadow-sm focus:border-brand-navy-400 focus:outline-none"
            />
            <Button
              size="sm"
              variant={showAdd ? 'ghost' : 'gold'}
              onClick={() => {
                setShowAdd((v) => !v);
                setAddQuery('');
              }}
            >
              {showAdd ? 'Close' : '+ Add field'}
            </Button>
          </div>
        }
      />

      {showAdd && (
        <div className="border-b border-slate-100 bg-slate-50 px-4 py-3">
          <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            Add a field that is not already being pulled
          </label>
          <p className="mt-1 text-xs text-slate-500">
            {notPulled.length} fields available. Start typing to search the UWM Insights API catalog.
          </p>
          <div className="relative mt-2">
            <input
              type="text"
              autoFocus
              value={addQuery}
              onChange={(e) => setAddQuery(e.target.value)}
              placeholder="e.g. Outstanding, Borrower, CreditScore…"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-navy-400 focus:outline-none"
            />
            {trimmedAdd && suggestions.length > 0 && (
              <div className="absolute z-10 mt-1 max-h-72 w-full overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg">
                {suggestions.map((s) => (
                  <button
                    key={s.fieldName}
                    type="button"
                    disabled={togglePulled.isPending}
                    onClick={() => {
                      // Two cases:
                      //   1. Row exists but manuallyDisabled=true → re-enable
                      //      it (and clear extraIncluded confusion).
                      //   2. Row does not exist OR exists but extraIncluded=false
                      //      → set extraIncluded=true so it joins the pull list.
                      const args: {
                        fieldName: string;
                        manuallyDisabled?: boolean;
                        extraIncluded?: boolean;
                      } = { fieldName: s.fieldName };
                      if (s.manuallyDisabled) args.manuallyDisabled = false;
                      if (!s.isBaseline) args.extraIncluded = true;
                      togglePulled.mutate(args, {
                        onSuccess: () => {
                          setAddQuery('');
                        },
                      });
                    }}
                    className="flex w-full items-center justify-between gap-3 border-b border-slate-100 px-3 py-2 text-left last:border-b-0 hover:bg-brand-cream-100"
                  >
                    <span className="font-mono text-xs text-brand-navy-800">
                      {s.fieldName}
                    </span>
                    <span className="text-[10px] uppercase tracking-wider text-slate-400">
                      {s.isBaseline
                        ? s.manuallyDisabled
                          ? 'Disabled — re-enable'
                          : 'Baseline'
                        : 'Add to pull'}
                    </span>
                  </button>
                ))}
              </div>
            )}
            {trimmedAdd && suggestions.length === 0 && (
              <p className="mt-2 text-xs text-slate-500">
                No catalog fields match “{addQuery}”.
              </p>
            )}
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-2.5">Field</th>
              <th className="px-4 py-2.5">Source</th>
              <th className="px-4 py-2.5">UWM supported</th>
              <th className="px-4 py-2.5">Notes</th>
              <th className="px-4 py-2.5 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {fields.isLoading && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-sm text-slate-500">
                  Loading…
                </td>
              </tr>
            )}
            {!fields.isLoading && visiblePulled.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-sm text-slate-500">
                  {activeSearch
                    ? `No pulled fields match “${activeSearch}”.`
                    : 'No fields are currently being pulled.'}
                </td>
              </tr>
            )}
            {visiblePulled.map((f) => (
              <tr key={f.fieldName} className="border-t border-slate-100">
                <td className="px-4 py-2 font-mono text-xs text-brand-navy-800">
                  {f.fieldName}
                </td>
                <td className="px-4 py-2">
                  <Badge tone={f.isBaseline ? 'neutral' : 'info'}>
                    {f.isBaseline ? 'Baseline' : 'Added by admin'}
                  </Badge>
                </td>
                <td className="px-4 py-2">
                  <Badge tone={f.supported ? 'success' : 'danger'}>
                    {f.supported ? 'Yes' : 'No (rejected)'}
                  </Badge>
                </td>
                <td className="px-4 py-2 max-w-[280px] text-xs text-slate-500">
                  {f.rejectionNote && (
                    <span className="block text-rose-600">UWM: {f.rejectionNote}</span>
                  )}
                  {f.adminNote && <span className="block">Admin: {f.adminNote}</span>}
                </td>
                <td className="px-4 py-2 text-right">
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={togglePulled.isPending}
                    onClick={() => {
                      // Removing semantics:
                      //   - Baseline field → manuallyDisabled=true
                      //   - Admin-added field → extraIncluded=false (drop it)
                      const args: {
                        fieldName: string;
                        manuallyDisabled?: boolean;
                        extraIncluded?: boolean;
                      } = { fieldName: f.fieldName };
                      if (f.isBaseline) args.manuallyDisabled = true;
                      else args.extraIncluded = false;
                      togglePulled.mutate(args);
                    }}
                  >
                    {f.isBaseline ? 'Disable' : 'Remove'}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ===========================================================================
// Contacts manager — add / edit / soft-delete the org roster used by Pipeline
// assignments + workflow recipient resolution.
// ===========================================================================

interface AdminContact {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  whatsappPhone: string | null;
  contactType: ContactType;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

const CONTACT_TYPE_OPTIONS: ContactType[] = [
  'loan_officer',
  'assistant',
  'processor',
  'title',
  'borrower',
  'underwriter',
  'realtor',
  'attorney',
  'other',
];

function ContactsManager() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<AdminContact | null>(null);
  const [creating, setCreating] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);

  const list = useQuery<{ contacts: AdminContact[] }>({
    queryKey: ['admin-contacts'],
    queryFn: () => api('/api/admin/contacts'),
  });

  const remove = useMutation({
    mutationFn: (contactId: string) =>
      api(`/api/admin/contacts/${contactId}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-contacts'] });
      qc.invalidateQueries({ queryKey: ['pipeline-contacts'] });
    },
  });

  const filtered = (list.data?.contacts ?? []).filter((c) => includeInactive || c.active);

  return (
    <Card>
      <CardHeader
        title="Contacts"
        subtitle="LOA, Processor, Title company, Borrower, MLO, and other org roster used in Pipeline assignment + workflow notifications."
        actions={
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1 text-xs text-slate-600">
              <input
                type="checkbox"
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
                className="h-3.5 w-3.5 rounded border-slate-300 text-brand-navy-600"
              />
              Show inactive
            </label>
            <Button size="sm" onClick={() => setCreating(true)}>
              + New contact
            </Button>
          </div>
        }
      />
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-2.5">Name</th>
              <th className="px-4 py-2.5">Type</th>
              <th className="px-4 py-2.5">Email</th>
              <th className="px-4 py-2.5">Phone</th>
              <th className="px-4 py-2.5">WhatsApp</th>
              <th className="px-4 py-2.5">Status</th>
              <th className="px-4 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-sm text-slate-500">
                  No contacts yet. Add one to use it in Pipeline assignments + workflow recipients.
                </td>
              </tr>
            )}
            {filtered.map((c) => (
              <tr key={c.id} className="border-t border-slate-100">
                <td className="px-4 py-2.5 font-medium text-brand-navy-800">{c.name}</td>
                <td className="px-4 py-2.5">
                  <Badge tone="info">{CONTACT_TYPE_LABELS[c.contactType] ?? c.contactType}</Badge>
                </td>
                <td className="px-4 py-2.5 text-slate-700">{c.email ?? '—'}</td>
                <td className="px-4 py-2.5 text-slate-700">{c.phone ?? '—'}</td>
                <td className="px-4 py-2.5 text-slate-700">{c.whatsappPhone ?? '—'}</td>
                <td className="px-4 py-2.5">
                  <Badge tone={c.active ? 'success' : 'neutral'}>
                    {c.active ? 'Active' : 'Inactive'}
                  </Badge>
                </td>
                <td className="px-4 py-2.5 text-right">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(c)}>
                    Edit
                  </Button>
                  {c.active && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        if (confirm(`Deactivate "${c.name}"?`)) remove.mutate(c.id);
                      }}
                    >
                      Deactivate
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(creating || editing) && (
        <ContactModal
          contact={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['admin-contacts'] });
            qc.invalidateQueries({ queryKey: ['pipeline-contacts'] });
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
    </Card>
  );
}

function ContactModal({
  contact,
  onClose,
  onSaved,
}: {
  contact: AdminContact | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const editing = !!contact;
  const [name, setName] = useState(contact?.name ?? '');
  const [contactType, setContactType] = useState<ContactType>(contact?.contactType ?? 'processor');
  const [email, setEmail] = useState(contact?.email ?? '');
  const [phone, setPhone] = useState(contact?.phone ?? '');
  const [whatsappPhone, setWhatsappPhone] = useState(contact?.whatsappPhone ?? '');
  const [active, setActive] = useState(contact?.active ?? true);
  const [err, setErr] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = {
        name,
        contactType,
        email: email.trim() || null,
        phone: phone.trim() || null,
        whatsappPhone: whatsappPhone.trim() || null,
        active,
      };
      return editing
        ? api(`/api/admin/contacts/${contact!.id}`, { method: 'PATCH', body: JSON.stringify(body) })
        : api('/api/admin/contacts', { method: 'POST', body: JSON.stringify(body) });
    },
    onSuccess: onSaved,
    onError: (e: Error) => setErr(e.message),
  });

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-6 shadow-floating">
        <div className="mb-4 flex items-start justify-between">
          <h3 className="font-display text-xl font-bold text-brand-navy-800">
            {editing ? 'Edit contact' : 'New contact'}
          </h3>
          <Button variant="ghost" onClick={onClose}>
            ✕
          </Button>
        </div>
        <div className="space-y-3">
          <Field label="Name">
            <Input value={name} onChange={(e) => setName(e.target.value)} required />
          </Field>
          <Field label="Type">
            <Select
              value={contactType}
              onChange={(e) => setContactType(e.target.value as ContactType)}
            >
              {CONTACT_TYPE_OPTIONS.map((t) => (
                <option key={t} value={t}>
                  {CONTACT_TYPE_LABELS[t]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Email">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="contact@example.com"
            />
          </Field>
          <Field label="Phone">
            <Input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+15551234567"
            />
          </Field>
          <Field label="WhatsApp phone (if different)">
            <Input
              value={whatsappPhone}
              onChange={(e) => setWhatsappPhone(e.target.value)}
              placeholder="Defaults to Phone above"
            />
          </Field>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-brand-navy-600"
            />
            Active (selectable in dropdowns)
          </label>
          {err && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
              {err}
            </div>
          )}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => save.mutate()}
            disabled={!name.trim() || save.isPending}
          >
            {save.isPending ? 'Saving…' : editing ? 'Save changes' : 'Create contact'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function AuditLog({ logs }: { logs: AuditEntry[] }) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  return (
    <Card>
      <CardHeader
        title="Audit log"
        subtitle="Latest 200 events. Click any row to inspect the full payload."
      />
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-2.5">When</th>
              <th className="px-4 py-2.5">Actor</th>
              <th className="px-4 py-2.5">Action</th>
              <th className="px-4 py-2.5">Entity</th>
              <th className="px-4 py-2.5">IP</th>
              <th className="px-4 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {logs.map((l) => {
              const hasDetail = !!(l.beforeJson || l.afterJson);
              return (
                <React.Fragment key={l.id}>
                  <tr
                    className={
                      'border-t border-slate-100 ' + (hasDetail ? 'cursor-pointer table-row-hover' : '')
                    }
                    onClick={() => hasDetail && setExpandedId(expandedId === l.id ? null : l.id)}
                  >
                    <td className="px-4 py-2 text-slate-500">
                      {new Date(l.createdAt).toLocaleString()}
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-slate-700">
                      {l.actorUserId ?? <span className="text-slate-400">(system)</span>}
                    </td>
                    <td className="px-4 py-2">
                      <Badge tone={auditTone(l.action)}>{l.action}</Badge>
                    </td>
                    <td className="px-4 py-2 text-slate-700">
                      {l.entityType ?? '—'}{' '}
                      {l.entityId ? (
                        <span className="font-mono text-xs text-slate-400">#{l.entityId.slice(0, 12)}</span>
                      ) : (
                        ''
                      )}
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-500">{l.ipAddress ?? ''}</td>
                    <td className="px-4 py-2 text-right">
                      {hasDetail && (
                        <span className="text-xs text-slate-400">
                          {expandedId === l.id ? '▼' : '▶'}
                        </span>
                      )}
                    </td>
                  </tr>
                  {expandedId === l.id && hasDetail && (
                    <tr className="bg-slate-50/60">
                      <td colSpan={6} className="px-4 py-3">
                        {l.beforeJson && (
                          <div className="mb-2">
                            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                              Before
                            </div>
                            <JsonView value={l.beforeJson} />
                          </div>
                        )}
                        {l.afterJson && (
                          <div>
                            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                              After
                            </div>
                            <JsonView value={l.afterJson} />
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function auditTone(action: string): 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'gold' {
  if (action.includes('failed')) return 'danger';
  if (action.includes('signin')) return 'info';
  if (action.startsWith('workflow')) return 'gold';
  if (action.startsWith('sync.completed')) return 'success';
  if (action.startsWith('sync')) return 'info';
  if (action.startsWith('integration')) return 'gold';
  if (action.startsWith('notification')) return 'success';
  return 'neutral';
}
